//@ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { AppConstants, LMTimeStampDirective, LMTransformOptions, LMToolbarActionTypeEnum } from '@utils/app.constants';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMProcessIndicatorService } from '../../field/processindicator/lm-process-indicator.service';
import {LMContextService, LMUrlUtilService} from '@app/services';
import { LMGlobalPersonalizationService } from '@app/states';

@Injectable()
export class LMListCardService {
  // todo would like provide services w/o state to app.component. Can't do that here because
  // LMFormatterService does have state. And it cannot be provided at a lower level than
  // the LMListCardService

  private _cardWidth: number;

  // Keep track of heights for each section
  // Use this to set section to the largest height
  private sectionedCardHeightMap = new Map();

  // Default based on new theme using non-compact layout
  private sectionedCardLineHeight = 22;

  constructor(
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFormatterService: LMFormatterService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmThemeService: LMThemeService,
    private lmProcessIndicatorService: LMProcessIndicatorService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmContextService: LMContextService
  ) {
    const themeId = lmThemeService.getCurrentThemeId();

    if (themeId?.includes('classic')) {
      this.sectionedCardLineHeight = 18;
    }

    // sets default to 14px
    if (themeId?.includes('new')) {
      if (this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout) {
        this.sectionedCardLineHeight = 20;
      }
    }
  }

  public getCardTemplateData(
    displayField: LMListDisplayField,
    dataView: LMDataView,
    automationId: string,
    openActionRestricted?: boolean,
    isFilterList?: boolean,
    isCardViewWithDetailSections?: boolean,
    isSelectList?: boolean
  ): LMCardTemplateData {
    try {
      let buttonRow: Array<LMToolbarButton>;
      let buttonMenu: Array<LMToolbarButton>;

      if (displayField.buttonRow) {
        buttonRow = this.getCardButtons(displayField.buttonRow, dataView);
      }

      if (displayField.buttonMenu) {
        buttonMenu = this.getCardButtons(displayField.buttonMenu, dataView, 'button-menu-item');
      }

      let cardTemplate: LMCardTemplateData = {
        buttonRowAlignment: displayField.buttonRowAlignment,
        columns : [
          {
            align: displayField.mainColumnAlignment,
            column: this.getDisplayFieldValues(displayField.mainColumn, dataView, displayField.showLabels),
            percent: displayField.mainColumnPercent
          },
          {
            align: displayField.rightColumnAlignment,
            column: this.getDisplayFieldValues(displayField.rightColumn, dataView, displayField.showLabels),
            percent: displayField.rightColumnPercent
          }
        ],
        buttonRow,
        buttonMenu
      };

      if (cardTemplate.buttonMenu === undefined
        && !openActionRestricted
        && !isFilterList
        && !isSelectList
        && !isCardViewWithDetailSections
        && (this.lmContextService?.isSmartPhone() || this.lmContextService?.inNativeMobileApp())
      ) {
        cardTemplate.displayButtonMenuAsChevron = true;
      }

      if (displayField.leftColumn) {
        cardTemplate = this.getLeftColumnTemplateInfo(displayField.leftColumn, dataView, cardTemplate);
      }

      if (displayField.leftColumns) {
        const valid: Array<LMCardViewLeftColumn> = [];
        const inValid: Array<LMCardViewLeftColumn> = [];
        const noCondition: Array<LMCardViewLeftColumn> = [];

        displayField.leftColumns.forEach((leftColumn: LMCardViewLeftColumn) => {
          if (leftColumn.condition) {
            const isValid = this.lmConditionUtilService.isValid([ leftColumn.condition ], dataView);
            if (isValid) {
              valid.push(leftColumn);
            } else {
              inValid.push(leftColumn);
            }
          } else {
            noCondition.push(leftColumn);
          }
        });

        if (valid.length > 0) {
          const missingImage = dataView?.fields[valid[0]?.fieldId]?.blobUrl === '';
          cardTemplate = this.getLeftColumnTemplateInfo(valid[0], dataView, cardTemplate, missingImage ? 'hidden' : 'visible');
        }

        if (inValid.length > 0 && inValid.length === displayField.leftColumns.length) {
          cardTemplate = this.getLeftColumnTemplateInfo(inValid[0], dataView, cardTemplate, 'hidden');
        }

        if (noCondition.length === displayField.leftColumns.length
          || ((noCondition.length + inValid.length) === displayField.leftColumns.length && noCondition.length > 0)
        ) {
          cardTemplate = this.getLeftColumnTemplateInfo(noCondition[0], dataView, cardTemplate);
        }
      }

      if (automationId && dataView.id) {
        cardTemplate.automationId = `lm-${automationId}-card-${dataView.id}`;
      }

      return cardTemplate;

    } catch (e) {
      this.lmErrorUtilService.handleError(e.message, e.stack);
    }
  }

  private getLeftColumnTemplateInfo(
    leftColumn: LMCardViewLeftColumn,
    dataView: LMDataView,
    cardTemplate: LMCardTemplateData,
    imageVisibility?: 'visible' | 'hidden'
  ): LMCardTemplateData {
    const isMissingImage = this.getIsMissingImage(leftColumn, dataView);
    const isRandomBackground = this.getIsRandomBackgroundImage(leftColumn, dataView);
    const foregroundText = this.getImageForegroundText(leftColumn, dataView);

    cardTemplate.image = {
      bgColorClass: this.getBackgroundColorClass(leftColumn, dataView, foregroundText),
      cardImageClasses: this.getCardImageClasses(leftColumn, imageVisibility),
      displayAsLink: leftColumn?.httpUrl && !leftColumn?.largeImage ? true : false,
      displayAsNavigation: leftColumn?.navigation && !leftColumn?.largeImage ? true : false,
      openInOverlay: leftColumn?.openInOverlay,
      navigationTargetComponent: leftColumn?.navigationTargetComponent,
      href: this.getMsgRuleValue(leftColumn?.httpUrl, dataView, true),
      imageForegroundText: foregroundText,
      imageUrl: this.getLeftColumnImageUrl(leftColumn, dataView),
      isMissingImage: isMissingImage,
      isRandomBackground: isRandomBackground,
      largeImage: this.getLargeImage(leftColumn, dataView),
      navigationParams: this.getCardImageNavigation(leftColumn, dataView),
      openExternal: leftColumn.openExternal,
      fieldId: leftColumn.fieldId
    };

    return cardTemplate;
  }

  public getSectionedCardData(sectionedCard: LMSectionedCard, dataView: LMDataView, fields: LMSpecFields): Array<LMSectionedCardTemplate> {
    const data: Array<LMSectionedCardTemplate> = [];

    sectionedCard.sections.forEach((section, index) => {

      if (section.imageSection && Object.keys(section.imageSection).length > 0) {
        data.push(this.getImageSectionTemplateInfo(section.imageSection, dataView, fields));
      }

      if (section.titleSection) {
        data.push(this.getTitleSectionTemplateInfo(section.titleSection, dataView));
      }

      if (section.rowSection) {
        data.push(this.getRowSectionTemplateInfo(section.rowSection, dataView, index));
      }

      if (section.fieldSection) {
        data.push(this.getFieldSectionTemplateInfo(section.fieldSection, dataView, index));
      }

      if (section.actionSection) {
        const actions = this.getActionsForSection(section.actionSection.actions, dataView);
        data.push({ 
          sectionType: 'action', 
          section: { actions },
          sectionHeight: this.sectionedCardLineHeight 
        });
      }
    });

    return data;
  }

  public get cardWidth(): number {
    return this._cardWidth;
  }

  public set cardWidth(width: number) {
    this._cardWidth = width;
  }

  private getBackgroundColorClass(
    leftColumn: LMCardViewLeftColumn,
    dataView: LMDataView,
    foregroundText: string
  ) {
    if (leftColumn === undefined || !this.getIsMissingImage(leftColumn, dataView)) {
      return null;
    }

    return `image-initials large ${this.lmThemeService.getUniqueBackgroundColorClass(foregroundText)}`;
  }

  private getIsRandomBackgroundImage(
    leftColumn: LMCardViewLeftColumn,
    dataView: LMDataView
  ): boolean {
    if (leftColumn === undefined || !this.getIsMissingImage(leftColumn, dataView)) {
      return null;
    }

    return leftColumn.isRandomBackground;
  }

  private getIsMissingImage(
    leftColumn: LMCardViewLeftColumn,
    dataView: LMDataView
  ): boolean {
    if (leftColumn === undefined || dataView === undefined) {
      return null;
    }

    return dataView.fields[leftColumn.fieldId] ? dataView.fields[leftColumn.fieldId].missingImage : null;
  }

  private getImageForegroundText(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): string {
    if (leftColumn === undefined || leftColumn.foregroundText === undefined) {
      return null;
    } else if (leftColumn.foregroundText) {
      // Display a max of 3 characters
      const text = this.lmFormatUtilService.formatMessageRule(leftColumn.foregroundText, dataView);
      return this.lmThemeService.getImageForegroundText(text);
    }

    return null;
  }

  private getCardImageClasses(leftColumn: LMCardViewLeftColumn, imageVisibility?: 'visible' | 'hidden'): string {
    const classes = [ 'lm-card-image' ];

    if (leftColumn && leftColumn.displayImageAs) {
      const orientationClass = 'lm-' + leftColumn.displayImageAs + '-image';
      classes.push(orientationClass);
    }

    if (leftColumn?.largeImage) {
      classes.push('lm-large-image-is');
    }

    if (imageVisibility === 'visible') {
      classes.push('lm-visible');
    }

    if (imageVisibility === 'hidden') {
      classes.push('lm-hidden');
    }

    return classes.join(' ');
  }

  private getImageSectionImageUrl(imageSection: LMSectionedCardImageSectionImage, dataView: LMDataView): string {
    const blobUrl = dataView.fields[imageSection.fieldId]?.blobUrl as string;
    const url = blobUrl === undefined ? '../lib/landmark/images/missing-picture-96px.png' : String(blobUrl);

    // Set height to match missing image height (96px) if lines are undefined
    // See buildComponentJson in FormViewBuilderBase.java
    const height = imageSection.lines ? (imageSection.lines * this.sectionedCardLineHeight) : 96;
    const size = `${this.cardWidth}x${height}`;

    return this.lmUrlUtilService.updateUrlParams(url, { size });
  }

  private getLeftColumnImageUrl(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): string {
    if (leftColumn === undefined || dataView === undefined) {
      return null;
    }

    // Use random background image and not the missing image url
    if (leftColumn.isRandomBackground
      && leftColumn.foregroundText
      && (dataView.fields[leftColumn.fieldId] && dataView.fields[leftColumn.fieldId].missingImage)
    ) {
      return null;
    }

    if(leftColumn.fieldId && dataView.fields[leftColumn.fieldId] && dataView.fields[leftColumn.fieldId].blobUrl) {
      const blobUrl = dataView.fields[leftColumn.fieldId].blobUrl;
      return blobUrl === '' ? '../lib/landmark/images/missing-picture-96px.png' : String(blobUrl);
    }

    if (leftColumn.fieldId && dataView.fields[leftColumn.fieldId]) {
      return String(dataView.fields[leftColumn.fieldId].value);
    } else if (leftColumn.fieldId === undefined && dataView.representativeImageUrl) {
      return dataView.representativeImageUrl;
    } else {
      return '';
    }
  }

  private getLargeImage(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): LMLargeImageIsInfo {
    if (leftColumn === undefined || leftColumn.largeImage === undefined || dataView === undefined) {
      return null;
    }

    const cardLargeImage: LMLargeImageIsInfo = {};

    if (leftColumn?.largeImage?.fieldId) {
      const largeImage = dataView.fields[leftColumn.largeImage.fieldId];
      cardLargeImage.imgUrl = largeImage?.blobUrl;
    }

    if (leftColumn?.largeImage?.title) {
      const largeImage = dataView.fields[leftColumn.largeImage.title.fieldId];
      cardLargeImage.title = largeImage?.value;
    }

    if (leftColumn?.largeImage?.download) {
      cardLargeImage.download = {};

      const field = dataView.fields[leftColumn.largeImage.download.fieldId];
      cardLargeImage.download.downloadUrl = field.blobUrl as string;
    }

    return cardLargeImage;
  }

  private getDisplayFieldValues(
    displayFields: Array<LMListDisplayField>,
    dataView: LMDataView,
    showLabels?: boolean
  ): Array<LMListViewTemplateField> {

    const values: Array<LMListViewTemplateField> = [];

    if (displayFields === undefined || dataView === undefined) {
      return values;
    }

    displayFields.forEach((displayField: LMListDisplayField) => {
      const field: LMField = dataView.fields[this.getFieldID(displayField)];
      const label: string = displayField.label && !displayField.labelSuppressed ? this.lmFormatUtilService.formatMessageRule(displayField.label, dataView) : '';
      const isBlankLine = displayField.type === undefined && displayField.isBlankLine;
      const val = field ? field.value : '';
      const displayFieldType = isBlankLine ? 'BLANKLINE' : displayField.type.toUpperCase();
      const transformOptions: LMTransformOptions = {};

      if (displayField.isBlankLine) {
        values.push({ type: displayFieldType, displayValue: '' });
        return;
      }

      if (displayField.visibility && !this.lmConditionUtilService.isValid(displayField.visibility, dataView)) {
        return;
      }

      if (displayFieldType === AppConstants.BCD && field?.precision) {
        transformOptions.precision = (displayField.numberOfDecimals || displayField.numberOfDecimals === 0) ? displayField.numberOfDecimals : field.precision;
      }

      if (displayFieldType === AppConstants.BCD && displayField.decSize) {
        transformOptions.decSize = (displayField.numberOfDecimals || displayField.numberOfDecimals === 0) ? displayField.numberOfDecimals : displayField.decSize;
      }

      if (displayFieldType === AppConstants.BCD && displayField.showPercentSymbol) {
        transformOptions.showPercentSymbol = displayField.showPercentSymbol;
      }

      if (displayFieldType === AppConstants.BCD && displayField.currencySymbolInfo) {
        transformOptions.currencySymbolIs = this.lmFormatUtilService.getCurrencySymbol(displayField.currencySymbolInfo, dataView);
      }

      if ((displayFieldType === AppConstants.BCD && displayFieldType === AppConstants.BCD) && displayField.displayNegativeAmountAs === 'parens') {
        transformOptions.displayNegativeParens = displayField.displayNegativeAmountAs;
      }

      if (displayField?.timeZoneIs) {
        transformOptions.timeZone = dataView?.fields[displayField.timeZoneIs].value as string;
      }

      if (displayField?.displayWithTimeZone) {
        transformOptions.displayWithTimeZone = true;
      }

      if (displayFieldType === AppConstants.TIMESTAMP && (displayField?.displayDateAndHoursAndMinutes || displayField?.limitToMinuteIncrements)) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES;
      }

      if (displayFieldType === AppConstants.TIMESTAMP && displayField?.disableDST) {
        transformOptions.disableDST = true;
      }

      if (displayFieldType === AppConstants.PERCENT && displayField.numberOfDecimals !== undefined) {
        transformOptions.precision = displayField.decSize;
      }

      const displayValue = this.getFieldDisplayValue(displayFieldType, String(val), displayField, dataView, transformOptions);
      const displayAsTag = this.lmConditionUtilService.displayAsTag(displayField, dataView, displayValue);

      // [use text area [of <Literal> lines]]	// use a text area control to display this field
      // [show up to <Literal> characters]	// default is 50
      const lines = displayField.lines ? displayField.lines : displayField.showChars ? this.showCharsToLines(displayField.showChars) : null;

      const templateField: LMListViewTemplateField = {
        fieldId: displayField.fieldId,
        type: displayFieldType,
        label: showLabels ? label : null,
        navigationParams: displayField.linkNavigation ? { navigationName: displayField.linkNavigation, dataView } : null,
        openInOverlay: displayField.openInOverlay,
        navigationTargetComponent: displayField.navigationTargetComponent,
        alertCondition: this.getAlertCondition(displayField, dataView),
        httpUrl: displayField.httpUrl ? this.getMsgRuleValue(displayField.httpUrl, dataView) : null,
        value: this.getFieldValue(displayFieldType, String(val), undefined, transformOptions),
        openExternal: displayField.openExternal ? displayField.openExternal : null,
        displayValue,
        displayAsTag,
        icon: displayField.icon ? this.getIcon(displayField) : null,
        lines,
        height: lines === null ? (1 * this.sectionedCardLineHeight) : (lines * this.sectionedCardLineHeight)
      };

      if (displayField.displayAsAlert) {
        templateField.displayAsAlert = this.formatDisplayAsAlert(displayField);
      }

      if (displayField.displayAsProgressBar) {
        templateField.progressFieldData = <LMProgressFieldData>{
          value: this.getFieldValue(displayFieldType, Number(val), displayField.meterRangeValue),
          displayValue: templateField.displayValue,
          meterRangeValue: displayField.meterRangeValue,
          ranges: displayField.ranges,
          dataView,
          field,
          displayPercent: AppConstants.PERCENT === displayField.type
        };
      }

      if (displayField.displayAsRating) {
        templateField.ratingOptions = this.lmFormatUtilService.formatStarRatingOptions(val as number, displayField.id);
        templateField.displayAsRating = true;
      }

      templateField.isField = this.isField(templateField, displayField);
      templateField.isFormText = (templateField.type === AppConstants.FORMTEXT);
      templateField.isInternalNavigation = this.isInternalNavigation(templateField);
      templateField.isExternalNavigation = this.isExternalNavigation(templateField);
      templateField.isProcessIndicator = this.isProcessIndicator(displayField);

      if (templateField.isProcessIndicator) {
        const value = dataView.fields[displayField.fieldId].value;

        if (value === '') {
          return;
        }

        const processIndicatorData = JSON.parse(value.toString());
        templateField.processIndicatorDisplayData = this.getProcessIndicatorData(<LMProcessIndicatorData>processIndicatorData);
      }

      if (templateField.type === AppConstants.FORMBUTTON || templateField.type === AppConstants.NAVIGATION
      || (templateField.type === AppConstants.SUBFORM && displayField?.subFormAction)) {
        const buttonSpec = displayField as LMFormButtonSpec;
        templateField.isFormButton = true;
        const button: LMToolbarButton = this.getCardButtons([ buttonSpec ], dataView)[0];
        templateField.cardButton = button;
      }

      if (displayAsTag) {
        if (displayField.tagStyle && displayField.tagStyle.length > 0) {
          templateField.fieldStyle = this.getFieldStyle(displayField, dataView);
        }
      } else {
        templateField.fieldStyle = { ...displayField.fieldStyle };
      }

      templateField.textStyle = { ...displayField.textStyle };
      templateField.isRichText = displayField.isRichText;

      values.push(templateField);
    });

    return values;
  }

  private getFieldStyle(field: LMListDisplayField, dataView): LMFieldStyle {
    const tagStyle = field.tagStyle.find(item => {
      if (!item.condition) {
        return true;
      }

      return this.lmConditionUtilService.isValid([ item.condition ], dataView);
    });

    return  { ...field.fieldStyle, ...tagStyle.style };
  }

  private getTagTemplate(imageSection: LMSectionedCardImageSectionImage, dataView, fields: LMSpecFields): LMSectionedCardTagTemplate {
    const tagTemplate: LMSectionedCardTagTemplate = { isValid: true, style: {} };
    const stateValues = fields[imageSection?.tagValue?.fieldId]?.stateValues;

    imageSection?.tag?.colors?.forEach((tagColor) => {
      // Set color based on condition
      if (tagColor.condition ) {
        const colorIsValid = this.lmConditionUtilService.isValid([ tagColor.condition ], dataView);
        if (colorIsValid) {
          tagTemplate.style.color = tagColor.color as LMStyleColor;
        }
      } else {
        tagTemplate.style.color = tagColor.color as LMStyleColor;
      }
    });

    // Set tag visibility
    if (imageSection?.tag?.condition) {
      tagTemplate.isValid = this.lmConditionUtilService.isValid([ imageSection.tag.condition ], dataView);
    }

    if (imageSection.tag) {
      let tagValue = '';

      if (imageSection.tagValue.type === 'field') {
        const tagAsDisplayField: LMListDisplayField = {
          fieldId: imageSection.tagValue.fieldId,
          type: imageSection.tagValue.type,
        };

        if (stateValues) {
          tagAsDisplayField.states = stateValues;
        }

        tagValue = this.getDisplayFieldValues([ tagAsDisplayField ], dataView)[0].displayValue as string;
      }

      if (imageSection.tagValue.type === 'value') {
        tagValue = imageSection.tagValue.value;
      }

      tagTemplate.tagValue = tagValue;

      if (tagTemplate.tagValue === '') {
        tagTemplate.isValid = false;
      }
    } else {
      tagTemplate.isValid = false;
    }

    return tagTemplate;
  }

  private isField(templateField: LMListViewTemplateField, displayField: LMListDisplayField) {
    return (templateField.displayValue.trim().length > 0
      && !displayField.isProcessIndicator
      && !displayField.httpUrl
      && !templateField.navigationParams
      && !templateField.progressFieldData);
  }

  private isInternalNavigation(item: LMListViewTemplateField): boolean {
    if (
      item === undefined ||
      (item.value === '' && item.type !== AppConstants.FORMTEXT) ||
      item.navigationParams === undefined ||
      item.navigationParams === null
    ) {
      return false;
    }

    return true;
  }

  private isProcessIndicator(displayField: LMListDisplayField): boolean {
    return displayField.isProcessIndicator && (displayField.type === AppConstants.TEXTAREA || displayField.type === AppConstants.TEXT);
  }

  private isExternalNavigation(item: LMListViewTemplateField): boolean {
    if (
      item === undefined ||
      (item.httpUrl === undefined || item.httpUrl === null) ||
      (item.httpUrl.trim().length === 0)
    ) {
      return false;
    } else if (item.httpUrl) {
      return true;
    }
  }

  private getFieldID(displayField: LMListDisplayField): string {
    if (displayField.fieldId !== undefined) {
      return displayField.fieldId;
    } else {
      return this.getFieldIdFromVariables(displayField);
    }
  }

  private getFieldIdFromVariables(displayField: LMListDisplayField): string {
    let fieldId: string;

    for (const key in displayField) {
      if (
        displayField.hasOwnProperty(key) &&
        displayField[key].variables
      ) {
        fieldId = displayField[key].variables[0].fieldId;
      }
    }

    return fieldId !== undefined ? fieldId : '';
  }

  private getIcon(displayField: LMListDisplayField): LMListIcon {

    const temp: LMListIcon = {
      icon: displayField.icon,
      iconUrl: displayField.icon ? displayField.iconUrl  : '',
      sohoIcon: displayField.icon ? this.lmThemeService.isValidSohoIcon(displayField.icon) : false,
    };

    return temp;
  }

  private getFieldValue(
    type: string,
    val: string | number,
    meterRangeValue?: number,
    transformOptions?: LMTransformOptions
  ): string | number {
    const value = {
      [AppConstants.PERCENT]    : () => this.convertValueToPercent(Number(val), meterRangeValue),
      [AppConstants.NUMERIC]    : () => this.lmFormatUtilService.numericToData(val).toString(),
      [AppConstants.BCD]        : () => this.lmFormatUtilService.bcdToData(String(val), transformOptions),
      [AppConstants.TIMESTAMP]  : () => this.lmFormatUtilService.timestampToData(val, transformOptions),
      [AppConstants.YYYYMMDD]   : () => this.lmFormatUtilService.yyyymmddToData(val),
      [AppConstants.TIME]       : () => this.lmFormatUtilService.timeToData(val),
      [AppConstants.YYYYMM]     : () => this.lmFormatUtilService.yyyymmToData(val),
      [AppConstants.MMDD]       : () => this.lmFormatUtilService.mmddToData(val),
      [AppConstants.YYYY]       : () => this.lmFormatUtilService.yyyyToData(val),
      default                 : () => val
    };

    return (value[type] || value['default'])();
  }

  private convertValueToPercent(value: number, meterRangeValue: number): number {
    const range: number = meterRangeValue || 1.0;
    const num = Number( ((value / range) * 100).toFixed(2) );
    return num > 100 ? 100 : num;
  }

  private getFieldDisplayValue(
    type: string,
    val: string,
    displayField: LMListDisplayField,
    dataView: LMDataView,
    transformOptions?: LMTransformOptions
  ): string {
    if ('FORMBUTTON' === type) {
      return '';
    }
    // Angular sanitizes the value in span
    // val = this.domSanitizer.sanitize(SecurityContext.HTML, val);
    const fieldId = this.getFieldID(displayField);
    const format = this.lmFormatUtilService;

    if (displayField.displayWhen && displayField.displayMsgRule) {
      if (this.lmConditionUtilService.isValid([ displayField.displayWhen ], dataView)) {
        return this.lmFormatUtilService.formatMessageRule(displayField.displayMsgRule, dataView);
      }
    }
    let states: LMStateValue[];

    if (displayField.states) {
      states = displayField.states;
    }

    /**
     * Treat fields that have display as dropdown (even though no dropdown will be displayed on a card)
     * as if it where a user defined state and show the states display value.
     */
    if (displayField.isUserDefinedStateField || dataView.fields[fieldId]?.hasShowAsDropdownStates) {
      states = dataView.fields[fieldId] ? dataView.fields[fieldId].stateValues : [];
    }

    const value = {
      [AppConstants.TIMESTAMP]  : () => format.timestampToDisplay(val, transformOptions),
      [AppConstants.YYYYMMDD]   : () => format.yyyymmddToDisplay(val),
      [AppConstants.TIME]       : () => format.timeToDisplay(val),
      [AppConstants.YYYYMM]     : () => format.yyyymmToDisplay(val),
      [AppConstants.MMDD]       : () => format.mmddToDisplay(val),
      [AppConstants.YYYY]       : () => format.yyyyToDisplay(val),
      [AppConstants.PERCENT]    : () => states ? format.stateLabelToText(states, val) : format.percentToDisplay(val, transformOptions),
      [AppConstants.NUMERIC]    : () => states ? format.stateLabelToText(states, val) : format.numericToDisplay(val),
      [AppConstants.BCD]        : () => states ? format.stateLabelToText(states, val) : format.bcdToDisplay(val, transformOptions),
      [AppConstants.BOOLEAN]    : () => states ? format.stateLabelToText(states, val) : format.booleanToDisplay(val),
      [AppConstants.FORMTEXT]   : () => displayField.text ? this.getMsgRuleValue(displayField.text, dataView) : val,
      default                 : () => states ? format.stateLabelToText(states, val) : val
    };

    return (value[type] || value['default'])();
  }

  private getMsgRuleValue(messageRule: LMMessageRule | string, dataView: LMDataView, isHttpMessageRule?: boolean) {
    if (messageRule === undefined || dataView === undefined) {
      return '';
    }

    if (isHttpMessageRule) {
      return this.lmFormatUtilService.formatMessageRule(messageRule, dataView).replace('mailto:', '');
    } else {
      return this.lmFormatUtilService.formatMessageRule(messageRule, dataView);
    }
  }

  private getAlertCondition(displayField: LMListDisplayField, dataView: LMDataView): LMAlertCondition {
    let alertCondition: LMAlertCondition;

    if (!displayField) {
      return alertCondition;
    }

    if (displayField.alert) {
      if (!this.lmFormatterService || !this.lmThemeService) {
        return alertCondition;
      }

      const condition = this.lmFormatterService.getAlertCondition(displayField, dataView);
      alertCondition = condition && condition.icon !== undefined ? condition : null;

      if (alertCondition) {
        const isValidSohoIcon = this.lmThemeService.isValidSohoIcon(alertCondition?.icon);
        if (!isValidSohoIcon && alertCondition?.iconUrl) {
          alertCondition.icon = undefined;
        } else {
          alertCondition.iconUrl = undefined;
        }
      }
    }

    return alertCondition;
  }

  private formatDisplayAsAlert(displayField: LMListDisplayField): LMDisplayAsAlert {
    return {
      color: displayField.displayAsAlert.color ? `lm-display-as-alert lpl-${displayField.displayAsAlert.color.toLowerCase()}` : null,
      icon:  this.getStandardAlertIconName(displayField.displayAsAlert.color) // displayField.icon ? this.getIcon(displayField).icon : this.getStandardAlertIconName(displayField.displayAsAlert.color)
    };
  }

  private getStandardAlertIconName(color: string): string {
    let className = '';

    if (!color) {
      return className;
    }

    if (color.toLowerCase() === 'yellow') {
      className = 'alert';
    }

    if (color.toLowerCase() === 'green') {
      className = 'success';
    }

    if (color.toLowerCase() === 'blue') {
      className = 'info';
    }

    if (color.toLowerCase() === 'red') {
      className = 'error';
    }

    return className;
  }

  /*
    Used to get buttons for
        - buttons in card columns
        - card button row
        - card button menu
        - sectioned card action section
   */
  private getCardButtons(
    buttonSpecs: Array<LMFormButtonSpec>,
    dataView: LMDataView,
    automationIdSuffix?: string
  ): Array<LMToolbarButton> {

    const buttons: Array<LMToolbarButton> = [];

    if (buttonSpecs === undefined || dataView === undefined) {
      return buttons;
    }

    buttonSpecs.forEach((formButtonSpec) => {
      const t = formButtonSpec.type;

      if (
        (formButtonSpec.condition && !this.lmConditionUtilService.isValid([ formButtonSpec.condition ], dataView)) ||
        (formButtonSpec.visibility && !this.lmConditionUtilService.isValid(formButtonSpec.visibility, dataView))
      ) {
        return;
      }

      const automationId = automationIdSuffix ?
        `lm-${formButtonSpec.automationId}-${automationIdSuffix}` :
          `lm-${formButtonSpec.automationId}-buttonrow-button`;

      // DPB adding cardViewFormButtonSpec to handle busform button pushes from a list -- Phillip thinking of better architecture solution.
      const label = formButtonSpec.title ? this.lmFormatUtilService.formatMessageRule(formButtonSpec.title, dataView) : '';
      const httpUrl = this.getHttpUrl(formButtonSpec, dataView);
      const button: LMCardViewButton = {
        actionReference: {
          id: formButtonSpec.fieldId,
          index: formButtonSpec.actionSpec ? formButtonSpec.actionSpec.index : null,
          row: dataView.id - 1,
          type: formButtonSpec.type,
          label,
          validity: [ formButtonSpec.condition ],
          navigation: formButtonSpec.navigation,
          resourceId: dataView.resourceId,
          cardViewFormButtonSpec: formButtonSpec,
          tooltip: this.getToolTip(formButtonSpec, label),
          name: dataView.name,
          subformSpec: formButtonSpec.buttonSubForm,
          subformAction: formButtonSpec.subFormAction,
          suppressFormOpen: formButtonSpec.suppressFormOpen
        },
        automationId,
        httpUrl,
        isOpenExternal: formButtonSpec.openExternal,
        actionSpec: formButtonSpec.actionSpec,
        isDisplayAsLink: formButtonSpec.displayAsLink && !formButtonSpec.iconUrl,
        isIconOnly: formButtonSpec.iconUrl !== undefined,
        iconUrl: formButtonSpec.iconUrl,
        icon: this.lmThemeService.isValidSohoIcon(formButtonSpec.icon) ? formButtonSpec.icon : null,
        state: { disabled: false, visible: true },
        fieldStyle: formButtonSpec.fieldStyle,
        isPrimary: formButtonSpec.isPrimary,
        displayTooltip: label === ' ' ? false :  true
      };

      switch (t) {
        case AppConstants.BUTTON_TYPE_ACTION:
        case AppConstants.BUTTON_TYPE_FORMBUTTON:
        case AppConstants.BUTTON_TYPE_RELATED:
          buttons.push(button);
          break;
        case AppConstants.BUTTON_TYPE_NAVIGATION :
          if (formButtonSpec.navigation !== undefined) {
            button.navigationParams = { navigationName: formButtonSpec.navigation, dataView };
          }
          buttons.push(button);
          break;
        case AppConstants.BUTTON_TYPE_SUBFORM :
          if (formButtonSpec.subFormAction) {
            buttons.push(button);
          }

          break;
      }
    });

    return buttons;
  }

  private getToolTip(formButtonSpec: LMFormButtonSpec, label: string): string {
    const tooltip = formButtonSpec.iconUrl !== undefined && label.trim().length !== 0 ? label : null;
    const httpUrl = formButtonSpec.httpUrl ? formButtonSpec.httpUrl.resourceValue : null;
    if (this.isMailToLink(httpUrl)) {
      // TODO: Localize
      return 'Send Email';
    }
    return tooltip;
  }

  private isMailToLink(value: string): boolean {
    if (!value) {
      return false;
    }

    return value.includes('mailto:');
  }

  private getHttpUrl(formButtonSpec: LMFormButtonSpec, dataView: LMDataView): string {
    let url = null;
    if (formButtonSpec.hasHttpUrl) {
      url = this.lmFormatUtilService.formatMessageRule(formButtonSpec.httpUrl, dataView);
    }

    return url;
  }

  private getProcessIndicatorData(processIndicatorData: LMProcessIndicatorData): Array<LMProcessIndicatorDisplayData> {
    return this.lmProcessIndicatorService.getTemplateData(processIndicatorData);
  }

  public encodeHtmlSingleQuote(button: LMCardViewButton): LMCardViewButton{
    const buttonCopy = button;

    if(button.actionReference?.cardViewFormButtonSpec?.title?.resourceValue) {
      buttonCopy.actionReference.cardViewFormButtonSpec.title.resourceValue = button.actionReference.cardViewFormButtonSpec.title.resourceValue.replace('\'', '&#39;');
    }

    return buttonCopy;
  }

  private showCharsToLines(size: number): number {
    return Math.ceil(size / 25);
  }

  private updateSectionHeightMap(fields: Array<LMListViewTemplateField|LMToolbarButton>, sectionIndex: number) {
    let sectionHeight = 0;
    fields.forEach((field) => {
      const templateField = field as LMListViewTemplateField;
      sectionHeight+= templateField.height;
    });

    const existingSection = this.sectionedCardHeightMap.get(`section-${sectionIndex}`);

    if (existingSection) {
      this.sectionedCardHeightMap.set(`section-${sectionIndex}`, { height: existingSection.height > sectionHeight ? existingSection.height : sectionHeight });
    } else {
      this.sectionedCardHeightMap.set(`section-${sectionIndex}`, { height: sectionHeight });
    }
  }

  private getActionsForSection(actions: Array<LMCheckControl|LMFormButtonSpec>, dataView: LMDataView): LMSectionedCardActionSectionActions {
    const sectionActions = {
      leftSection: [],
      rightSection: []
    };

    actions.forEach((action: LMFormButtonSpec|LMCheckControl) => {
      // @ts-ignore
      if (Object.values(LMToolbarActionTypeEnum).includes(action.type) || (action.type === 'subform' && action.subFormAction)) {
        const buttonSpec = action as LMFormButtonSpec;
        const buttonSpecs = this.getCardButtons([ buttonSpec ], dataView);

        // Card button rows could return empty array if action is not valid
        if (buttonSpecs.length > 0) {
          sectionActions.rightSection.push(buttonSpecs[0]);
        }
      }

      if (action.type === AppConstants.CHECKCONTROL) {
        sectionActions.leftSection.push(action);
      }
    });

    return sectionActions;
  }

  /**
   * Info used when rendering angular template for sectioned card
   * Should return only one image
   * If there are multiple valid images (default to the first valid)
   * @param imageSectionImages
   * @param dataView
   * @param fields
   * @private
   */
  private getImageSectionTemplateInfo(
    imageSectionImages: Array<LMSectionedCardImageSectionImage>,
    dataView: LMDataView,
    fields: LMSpecFields
  ): LMSectionedCardTemplate {
    const valid: Array<LMSectionedCardImageSectionImage> = [];
    const noCondition: Array<LMSectionedCardImageSectionImage> = [];

    let sectionTemplate: LMSectionedCardTemplate = {
      sectionType: 'image',
      section: {
        isMissingImage: true,
        imageUrl: null,
        lines: 1,
        tag: null
      },
      sectionHeight: this.sectionedCardLineHeight
    };

    imageSectionImages.forEach((imageSectionImage) => {
      if (imageSectionImage.condition) {
        const isValid = this.lmConditionUtilService.isValid([ imageSectionImage.condition ], dataView);
        if (isValid) {
          valid.push(imageSectionImage);
        }
      } else {
        noCondition.push(imageSectionImage);
      }
    });

    if (valid.length > 0) {
      const  imageSectionImage = valid[0];
      sectionTemplate = this.getImageSectionInfo(imageSectionImage, dataView, fields);
    } else if (noCondition.length > 0) {
      const imageSectionImage = noCondition[0];
      sectionTemplate = this.getImageSectionInfo(imageSectionImage, dataView, fields);
    }

    return sectionTemplate;
  }

  private getImageSectionInfo(
    imageSectionImage: LMSectionedCardImageSectionImage,
    dataView: LMDataView,
    fields: LMSpecFields
  ): LMSectionedCardTemplate {
    const sectionHeight = (imageSectionImage.lines ? imageSectionImage.lines : 1) * this.sectionedCardLineHeight;

    return {
      sectionType: 'image',
      section: {
        isMissingImage: this.getIsMissingImage(imageSectionImage, dataView),
        imageUrl: this.getImageSectionImageUrl(imageSectionImage, dataView),
        lines: imageSectionImage.lines,
        tag: this.getTagTemplate(imageSectionImage, dataView, fields)
      },
      sectionHeight
    };
  }

  private getTitleSectionTemplateInfo(titleSection: LMSectionedCardTitleSection, dataView: LMDataView): LMSectionedCardTemplate {
    const sectionTemplate: LMSectionedCardTemplate = {
      sectionType: 'title',
      section: {
        displayValue: this.lmFormatUtilService.formatMessageRule(titleSection.title, dataView),
        lines: titleSection.lines
      },
      sectionHeight: titleSection.lines * (this.sectionedCardLineHeight + 5)
    };

    if (titleSection.navigation) {
      sectionTemplate.section.navigationParams = { navigationName: titleSection.navigation, dataView };
    }

    return sectionTemplate;
  }

  private getRowSectionTemplateInfo(rowSection: LMSectionedCardRowSection, dataView: LMDataView, sectionIndex: number): LMSectionedCardTemplate {
    const fields = [];

    rowSection.fields.forEach((field) => {
      // Check if field is valid "visible when"
      if (field.visibility && !this.lmConditionUtilService.isValid(field.visibility, dataView)) {
        fields.push({ hidden: true, height: this.sectionedCardLineHeight });
      } else {
        if (field.navigation) {
          const action = field as LMFormButtonSpec;
          const actions = this.getActionsForSection([ action ], dataView);
          actions.leftSection.forEach((a) => fields.push(a));
          actions.rightSection.forEach((a) => fields.push(a));
        } else {
          fields.push(this.getDisplayFieldValues([ field ], dataView, true)[0]);
        }
      }
    });

    const sectionTemplate: LMSectionedCardTemplate = {
      sectionType: 'row',
      section: { fields }
    };

    this.updateSectionHeightMap(sectionTemplate.section.fields, sectionIndex);
    sectionTemplate.sectionHeight = this.sectionedCardHeightMap.get(`section-${sectionIndex}`).height;

    return sectionTemplate;
  }

  private getFieldSectionTemplateInfo(fieldSection: LMSectionedCardFieldSection, dataView: LMDataView, sectionIndex: number): LMSectionedCardTemplate {
    const fields = [];

    fieldSection.fields.forEach((field) => {
      // Check if field is valid "visible when"
      if (field.visibility && !this.lmConditionUtilService.isValid(field.visibility, dataView)) {
        fields.push({ hidden: true, height: this.sectionedCardLineHeight });
      } else {
        fields.push(this.getDisplayFieldValues([ field ], dataView, true)[0]);
      }
    });

    const sectionTemplate: LMSectionedCardTemplate = {
      sectionType: 'field',
      section: { fields }
    };

    this.updateSectionHeightMap(sectionTemplate.section.fields, sectionIndex);
    sectionTemplate.sectionHeight = this.sectionedCardHeightMap.get(`section-${sectionIndex}`).height;

    return sectionTemplate;
  }

  private getCardImageNavigation(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): LMCardNavigationParams {
    if (leftColumn?.navigation) {
      return { navigationName: leftColumn.navigation, dataView, openInOverlay: leftColumn.openInOverlay, navigationTargetComponent: leftColumn.navigationTargetComponent };
    }

    return null;
  }
}
